import { Button } from "@/components/ui/button";
import { contactPreferredAddress } from "@shared/contactEmailSelection";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { UserNotesPanel } from "@/components/notes/UserNotesPanel";
import TagInput from "@/components/ui/TagInput";
import { entityDetailKey } from "@/lib/queryKeys";
import { PersonIdentityCard } from "@/components/detail/PersonIdentityCard";
import { resolvePrimaryContactCompany } from "@/lib/contactIdentity";

interface ContactDrawerContentProps {
  id: number;
  onOpenFullProfile?: (id: number) => void;
}

/**
 * ContactDrawerContent
 * Anteprima rapida di un contatto nel RecordDrawer.
 * I dati arrivano da /api/contacts/:id (apiRequest restituisce JSON gia' parsato).
 * Consente modifica rapida dei campi principali e aggiunta note (notes_user).
 */
export default function ContactDrawerContent({ id, onOpenFullProfile }: ContactDrawerContentProps) {
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState({ firstName: "", lastName: "", role: "", email: "", phone: "" });

  const { data: contact, isLoading, isError } = useQuery({
    queryKey: entityDetailKey('contacts', id),
    queryFn: () => apiRequest(`/api/contacts/${id}`),
    enabled: id != null,
  });

  const updateMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      apiRequest("PATCH", `/api/contacts/${id}`, payload),
    onSuccess: (updated) => {
      queryClient.setQueryData(entityDetailKey('contacts', id), (current: any) => ({
        ...(current || {}),
        ...(updated || {}),
      }));
      queryClient.invalidateQueries({ queryKey: ["/api/contacts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/emails"] });
    },
    onError: () => {
      toast({ title: "Errore durante il salvataggio", variant: "destructive" });
    },
  });

  if (isLoading) {
    return <div className="p-5 text-muted-foreground">Caricamento...</div>;
  }

  if (isError || !contact) {
    return <div className="p-5 text-destructive">Errore caricamento contatto</div>;
  }

  const fullName = [contact.firstName, contact.middleName, contact.lastName]
    .filter(Boolean)
    .join(" ") || "Contatto senza nome";

  const primaryCompany = resolvePrimaryContactCompany(contact);
  const companyId = primaryCompany?.id;
  const companyName = primaryCompany?.name;

  const emails = [contact.companyEmail, contact.privateEmail].filter(Boolean);
  const phones = [contact.mobilePhone, (contact as any).officePhone, (contact as any).privatePhone].filter(Boolean);

  const startEditing = () => {
    setDraft({
      firstName: contact.firstName || "",
      lastName: contact.lastName || "",
      role: contact.jobTitle || (contact as any).role || "",
      email: contactPreferredAddress(contact) || "",
      phone: contact.mobilePhone || "",
    });
    setIsEditing(true);
  };

  const handleSave = () => {
    // Il ruolo vive su jobTitle oppure role: aggiorna il campo gia' valorizzato
    const roleField = contact.jobTitle != null ? "jobTitle" : "role";
    updateMutation.mutate(
      {
        firstName: draft.firstName.trim(),
        lastName: draft.lastName.trim(),
        [roleField]: draft.role.trim() || null,
        mobilePhone: draft.phone.trim() || null,
      },
      {
        onSuccess: () => {
          setIsEditing(false);
          toast({ title: "Contatto aggiornato" });
        },
      }
    );
  };


  return (
    <div className="flex flex-col">
      <div className="p-5 border-b border-border">
        <PersonIdentityCard
          name={fullName}
          details={{
            avatar: contact.avatar,
            role: primaryCompany?.role || contact.role,
            companyName,
            companyHref: companyId ? `/companies/${companyId}` : undefined,
            mobilePhone: contact.mobilePhone,
            email: contactPreferredAddress(contact),
          }}
        />
        <div className="mt-2 flex items-center gap-3">
          <button
            type="button"
            onClick={() => {
              if (onOpenFullProfile) onOpenFullProfile(id);
              else navigate(`/contacts/${id}`);
            }}
            className="text-sm font-medium text-primary hover:underline"
          >
            Apri scheda completa
          </button>
          {!isEditing && (
            <button
              type="button"
              onClick={startEditing}
              className="text-sm text-muted-foreground hover:text-foreground hover:underline"
            >
              Modifica
            </button>
          )}
        </div>
      </div>

      {isEditing ? (
        <div className="p-5 border-b border-border space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Nome</div>
              <Input
                value={draft.firstName}
                onChange={(e) => setDraft({ ...draft, firstName: e.target.value })}
              />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Cognome</div>
              <Input
                value={draft.lastName}
                onChange={(e) => setDraft({ ...draft, lastName: e.target.value })}
              />
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Ruolo</div>
            <Input
              value={draft.role}
              onChange={(e) => setDraft({ ...draft, role: e.target.value })}
            />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Email</div>
            <Input
              type="email"
              value={draft.email}
              readOnly
              aria-label="Email principale, gestisci dalla scheda completa"
            />
            <Button type="button" variant="link" onClick={() => navigate(`/contacts/${id}?tab=emails`)}>Gestisci indirizzi e email principale</Button>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Telefono</div>
            <Input
              value={draft.phone}
              onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={updateMutation.isPending}
              className="bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 rounded-lg"
            >
              Salva
            </button>
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              disabled={updateMutation.isPending}
              className="border border-border bg-background px-3 py-1.5 text-sm text-foreground hover:bg-muted rounded-lg"
            >
              Annulla
            </button>
          </div>
        </div>
      ) : (
        <>
          {(contact.jobTitle || (contact as any).role) && (
            <div className="p-5 border-b border-border">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Ruolo</div>
              <div className="text-foreground">{contact.jobTitle || (contact as any).role}</div>
            </div>
          )}

          {emails.length > 0 && (
            <div className="p-5 border-b border-border">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Email</div>
              {emails.map((email: string, i: number) => (
                <a key={i} href={`mailto:${email}`} className="block text-primary hover:underline">
                  {email}
                </a>
              ))}
            </div>
          )}

          {phones.length > 0 && (
            <div className="p-5 border-b border-border">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Telefono</div>
              {phones.map((phone: string, i: number) => (
                <div key={i} className="text-foreground">{phone}</div>
              ))}
            </div>
          )}
        </>
      )}

      {companyName && (
        <div className="p-5 border-b border-border">
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Azienda</div>
          {companyId ? (
            <button
              type="button"
              onClick={() => navigate(`/companies/${companyId}`)}
              className="text-primary hover:underline"
            >
              {companyName}
            </button>
          ) : (
            <span className="text-foreground">{companyName}</span>
          )}
        </div>
      )}

      <div className="p-5 border-b border-border">
        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Tag</div>
        <TagInput
          value={Array.isArray(contact.tags) ? contact.tags : []}
          onChange={(tags) => updateMutation.mutate({ tags })}
          compact
        />
      </div>

      <div className="p-5 border-b border-border">
        <UserNotesPanel entityType="contact" entityId={id} notesUser={contact.notes_user} />
      </div>
    </div>
  );
}
